import { useParams, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { learnerApi } from '@/api/b2b'
import { useAiqSession } from '@/hooks/useAiq'
import { AiqQuizStage, type AiqCompletionResult } from '@/components/learner/AiqQuizStage'
import { Play, Loader2, AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'

type Phase = 'intro' | 'quiz' | 'result'

interface QuizResult {
  totalQuestions: number
}

export default function PostAiqTest() {
  const { t } = useTranslation('learner')
  const { classProductId } = useParams<{ classProductId: string }>()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const [phase, setPhase] = useState<Phase>('intro')
  const [result, setResult] = useState<QuizResult | null>(null)

  // Get course info to retrieve productId (same format as MyCourses.tsx for cache sharing)
  const { data: coursesData, isLoading: isLoadingCourse } = useQuery({
    queryKey: ['myClassCourses'],
    queryFn: () => learnerApi.getMyClassCourses(),
    staleTime: 5 * 60 * 1000, // 5분간 캐시 유지 (페이지 이동 시 재요청 방지)
    gcTime: 10 * 60 * 1000, // 10분간 가비지 컬렉션 방지
  })

  const courses = coursesData?.data?.data || []
  const currentCourse = courses.find(
    (c) => c.classProductId === Number(classProductId)
  )
  const productId = currentCourse?.productId

  // AIQ Session hook
  const aiqSession = useAiqSession(
    'post',
    productId,
    classProductId ? Number(classProductId) : undefined
  )

  const handleStart = async () => {
    try {
      await aiqSession.startSession.mutateAsync()
      setPhase('quiz')
    } catch (error) {
      console.error('Failed to start Post-AIQ session:', error)
    }
  }

  const handleQuizComplete = (completionResult: AiqCompletionResult) => {
    setResult({
      totalQuestions: completionResult.totalQuestions,
    })
    setPhase('result')
  }

  const handleBack = () => {
    navigate('/my/aiq')
  }


  // Loading state
  if (isLoadingCourse) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('aiqTest.loading')}</p>
        </div>
      </div>
    )
  }

  // Course not found
  if (!currentCourse) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <p className="text-gray-600">{t('aiqTest.courseNotFound')}</p>
          <button
            onClick={handleBack}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            {t('aiqTest.goBack')}
          </button>
        </div>
      </div>
    )
  }

  // Intro Phase
  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
        <div className="max-w-lg w-full bg-white rounded-xl shadow-lg p-8">
          {/* AIQ BI */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-2">
              <img src="/aiq-logo.png" alt="AIQ" className="h-12" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('aiqTest.post.title')}
            </h1>
          </div>

          <div className="mb-8 space-y-4">
            <p className="text-base text-gray-600 leading-relaxed">
              {t('aiqTest.post.description')}
            </p>
            <p className="text-base text-gray-600 leading-relaxed">
              {t('aiqTest.testQuestions')}
            </p>
            <p className="text-sm text-gray-500 leading-relaxed">
              {t('aiqTest.resultNote')}
            </p>
          </div>

          {/* 문제 수 정보 */}
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border mb-8">
            <Play className="w-5 h-5 text-primary-600 flex-shrink-0" />
            <div>
              <div className="text-sm text-gray-600">{t('aiqTest.questionCount')}</div>
              <div className="text-lg font-semibold text-gray-900">{t('aiqTest.questionCountValue')}</div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleBack}
              className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('aiqTest.goBack')}
            </button>
            <button
              onClick={handleStart}
              disabled={aiqSession.isStarting}
              className="flex-1 py-3 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {aiqSession.isStarting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              {t('aiqTest.startTest')}
            </button>
          </div>

          {aiqSession.startError && (
            <p className="mt-4 text-sm text-red-600 text-center">
              {t('aiqTest.startFailed')}
            </p>
          )}
        </div>
      </div>
    )
  }

  // Quiz Phase - Use AiqQuizStage component
  if (phase === 'quiz' && aiqSession.sessionId && aiqSession.quizzes.length > 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <AiqQuizStage
          productId={productId || 0}
          testType="post"
          sessionId={aiqSession.sessionId}
          quizzes={aiqSession.quizzes}
          resumeFromIndex={aiqSession.resumeFromIndex}
          previousResponses={aiqSession.previousResponses}
          onComplete={handleQuizComplete}
          onBack={handleBack}
          submitResponse={aiqSession.submitResponse}
          completeSession={aiqSession.completeSessionFn}
        />
      </div>
    )
  }

  // Quiz loading state
  if (phase === 'quiz' && (!aiqSession.sessionId || aiqSession.quizzes.length === 0)) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('aiqTest.loadingQuiz')}</p>
        </div>
      </div>
    )
  }

  // Result Phase
  if (phase === 'result' && result) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
        <div className="max-w-lg w-full bg-white rounded-xl shadow-lg p-8">
          {/* AIQ BI */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-2">
              <img src="/aiq-logo.png" alt="AIQ" className="h-12" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('aiqTest.post.completedTitle')}
            </h1>
          </div>

          <div className="mb-8 space-y-4">
            <p className="text-base text-gray-600 leading-relaxed">
              {t('aiqTest.post.completedGreeting', { userName: user?.fullName })}
            </p>
            <p className="text-base text-gray-600 leading-relaxed">
              {t('aiqTest.post.completedMessage1')}
            </p>
            <p className="text-base text-gray-600 leading-relaxed">
              {t('aiqTest.post.completedMessage2')}
            </p>
            <p className="text-base text-gray-600 leading-relaxed">
              {t('aiqTest.post.completedMessage3')}
            </p>
            <p className="text-base text-gray-600 leading-relaxed">
              {t('aiqTest.post.thankYou')}
            </p>
          </div>

          {/* 결과 정보 */}
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border mb-8">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            <div>
              <div className="text-sm text-gray-600">{t('aiqTest.questionCount')}</div>
              <div className="text-lg font-semibold text-gray-900">{t('aiqTest.completedCount', { count: result.totalQuestions })}</div>
            </div>
          </div>

          <button
            onClick={handleBack}
            className="w-full py-3 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('aiqTest.goToAiq')}
          </button>
        </div>
      </div>
    )
  }

  return null
}
